import React from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

import type { SentimentTagFormValue } from '@/lib/sentiment';

export type SentimentTagDraftErrors = Partial<Record<'label', string>>;

type SentimentTagDialogProps = {
  open: boolean;
  isEditing: boolean;
  draft: SentimentTagFormValue;
  draftErrors: SentimentTagDraftErrors;
  setDraft: React.Dispatch<React.SetStateAction<SentimentTagFormValue>>;
  onOpenChange: (open: boolean) => void;
  onSave: () => void;
};

export const SentimentTagDialog = React.memo(
  ({
    open,
    isEditing,
    draft,
    draftErrors,
    setDraft,
    onOpenChange,
    onSave,
  }: SentimentTagDialogProps) => {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{isEditing ? 'Edit tag' : 'Add tag'}</DialogTitle>
            <DialogDescription>
              Define a sentiment label the model can assign to a call.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="sentiment-tag-label">Label</Label>
              <Input
                id="sentiment-tag-label"
                value={draft.label}
                onChange={event => {
                  setDraft(current => ({
                    ...current,
                    label: event.target.value,
                  }));
                }}
                placeholder="Frustrated"
              />
              {draftErrors.label && <p className="text-sm text-destructive">{draftErrors.label}</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="sentiment-tag-description">Description</Label>
              <Textarea
                id="sentiment-tag-description"
                value={draft.description}
                onChange={event => {
                  setDraft(current => ({
                    ...current,
                    description: event.target.value,
                  }));
                }}
                placeholder="Explain when this label should be applied."
                className="min-h-24"
              />
              <p className="text-xs text-muted-foreground">
                This description is passed to the model and should clearly explain when to apply
                this label.
              </p>
            </div>

            <DialogFooter>
              <Button variant="outline" type="button" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="button" onClick={onSave}>
                {isEditing ? 'Update tag' : 'Add tag'}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    );
  }
);
